import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { publicApi } from "../hooks/useApi";
import { AskAboutThis } from "../components/AskAboutThis";
import { Clock } from "lucide-react";

export default function AudioDetail() {
  const { id } = useParams();
  const { data: edition, isLoading } = useQuery({ queryKey: ["audio-edition", id], queryFn: async () => (await publicApi.get(`/audio/${id}`)).data });
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyDone, setNotifyDone] = useState(false);
  const [notifySubmitting, setNotifySubmitting] = useState(false);

  const notifyMe = async (e) => {
    e.preventDefault();
    if (!notifyEmail || notifySubmitting) return;
    setNotifySubmitting(true);
    try {
      await publicApi.post(`/leads?email=${encodeURIComponent(notifyEmail)}&source=audiobook_coming_soon`);
      setNotifyDone(true);
      setNotifyEmail("");
    } catch {
      // best-effort — silently leave the form as-is on failure
    }
    setNotifySubmitting(false);
  };

  if (isLoading || !edition) return <div className="max-w-3xl mx-auto px-6 py-24 text-white/70">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-16" data-testid="audio-detail-page">
      <div className="flex flex-col md:flex-row gap-10">
        <img src={edition.cover_image} alt="" className="w-56 aspect-square object-cover shrink-0 self-start" />
        <div className="flex-1">
          <p className="text-xs uppercase tracking-widest text-textSecondary mb-2">Deluxe Author Commentary</p>
          <h1 className="font-display text-display-sm md:text-display-md mb-4">{edition.title}</h1>
          <p className="text-white/70 max-w-xl">{edition.description}</p>
          <div data-testid="audio-rights-banner" className="flex items-start gap-2 mt-6 border border-white/20 bg-white/5 text-white/70 text-sm px-4 py-3 max-w-xl">
            <Clock className="w-4 h-4 shrink-0 mt-0.5" />
            <span>COMING SOON — this Deluxe Author Commentary edition is still in production. No release date has been set yet.</span>
          </div>
          <div className="flex gap-4 mt-6">
            <button data-testid="audio-purchase-button" disabled className="bg-white/20 text-white/75 px-6 py-3 text-sm uppercase tracking-wide cursor-not-allowed">
              Coming Soon
            </button>
          </div>
          {!notifyDone ? (
            <form onSubmit={notifyMe} className="flex flex-wrap gap-2 mt-4 max-w-xl">
              <input
                data-testid="audio-notify-email-input"
                type="email"
                required
                value={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.value)}
                placeholder="you@email.com"
                className="flex-1 min-w-[200px] bg-white/5 border border-white/15 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brandAccent"
              />
              <button data-testid="audio-notify-submit" type="submit" disabled={notifySubmitting} className="border border-white/40 px-4 py-2 text-xs uppercase tracking-wide hover:border-white transition-colors duration-200 disabled:opacity-60">
                {notifySubmitting ? "Submitting…" : "Notify Me"}
              </button>
            </form>
          ) : (
            <p data-testid="audio-notify-success" className="text-sm text-brandAccent mt-4">You're on the list — we'll email you when this edition is ready.</p>
          )}
          <div className="mt-5">
            <AskAboutThis type="audio_edition" refId={edition.id} title={edition.title} />
          </div>
        </div>
      </div>

      <div className="mt-12">
        <h2 className="text-xl mb-4">Segments — Coming Soon</h2>
        <ul className="divide-y divide-white/10 border-t border-b border-white/10">
          {edition.segments.map((s) => (
            <li key={s.id} className="py-3 flex justify-between items-center text-sm">
              <span>{s.title}</span>
              <span data-testid={`audio-segment-coming-soon-${s.id}`} className="text-white/50 text-xs uppercase tracking-wide">Coming Soon</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

